import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { LOGO_URL } from "@/data/site";
import { useContent } from "@/context/ContentProvider";
import { useConsultation } from "@/components/ConsultationProvider";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { open } = useConsultation();
  const { phones, phoneLinks } = useContent();
  const location = useLocation();
  const navigate = useNavigate();

  const goWhyEpoxy = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname === "/") {
      document.getElementById("why-epoxy")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: "why-epoxy" } });
    }
  };

  const links = [
    { to: "/", label: "Home", testid: "nav-link-home" },
    { to: "/services", label: "Services", testid: "nav-link-services" },
    { to: "/gallery", label: "Gallery", testid: "nav-link-gallery" },
    { to: "/guides", label: "Flooring Guides", testid: "nav-link-guides" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-stone-50/90 backdrop-blur-xl border-b border-amber-600/20 shadow-sm" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0" onClick={() => setMenuOpen(false)}>
          <img src={LOGO_URL} alt="Crownvera Epoxy logo" className="h-14 w-auto bg-stone-950 rounded-xl px-2.5 py-1.5 shadow-md" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-stone-700">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid} className="hover:text-amber-700 transition-colors duration-300 tracking-wide uppercase text-xs">
              {l.label}
            </Link>
          ))}
          <a href="/#why-epoxy" onClick={goWhyEpoxy} data-testid="nav-link-why-epoxy" className="hover:text-amber-700 transition-colors duration-300 tracking-wide uppercase text-xs">
            Why Epoxy
          </a>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a href={phoneLinks[0]} data-testid="nav-phone-1" className="flex items-center gap-2 text-sm text-stone-700 hover:text-amber-700 transition-colors duration-300">
            <Phone className="w-4 h-4 text-amber-700" /> {phones[0]}
          </a>
          <button
            onClick={open}
            data-testid="nav-consultation-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-md shadow-md transition-colors duration-300"
          >
            Free Consultation
          </button>
        </div>

        <button className="lg:hidden text-stone-800" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" data-testid="nav-mobile-menu-btn">
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-amber-600/15 bg-stone-50/95"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-4 text-sm">
              {links.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`${l.testid}-mobile`} onClick={() => setMenuOpen(false)} className="text-stone-800 uppercase tracking-wider text-xs font-medium">
                  {l.label}
                </Link>
              ))}
              <a href="/#why-epoxy" onClick={goWhyEpoxy} data-testid="nav-link-why-epoxy-mobile" className="text-stone-800 uppercase tracking-wider text-xs font-medium">
                Why Epoxy
              </a>
              <a href={phoneLinks[0]} data-testid="nav-phone-mobile" className="flex items-center gap-2 text-amber-700">
                <Phone className="w-4 h-4" /> {phones[0]}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
